import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import { ChecklistRow, emptyChecklistRow } from '../../utils/stationDefaults';

const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

interface Props {
    /** Prefix for the inputs' ids, unique on the page. */
    idPrefix: string;
    rows: ChecklistRow[];
    onChange: (rows: ChecklistRow[]) => void;
}

/**
 * The equipment checklist as a list of items, each with its German and
 * English label and an optional safety note (the shape the check screen reads,
 * #1604). It edits a draft only: the surrounding form saves it.
 */
const EquipmentChecklistEditor = ({ idPrefix, rows, onChange }: Props) => {
    const { t } = useTranslation();
    const update = (id: string, patch: Partial<ChecklistRow>) =>
        onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    const remove = (id: string) => onChange(rows.filter((row) => row.id !== id));

    const input = (row: ChecklistRow, field: 'labelDe' | 'labelEn' | 'noteDe' | 'noteEn', index: number) => {
        const id = `${idPrefix}-${index}-${field}`;
        return (
            <div className={'flex flex-col gap-1'}>
                <label className={'text-xs font-medium text-foreground/60'} htmlFor={id}>
                    {t(`equipment_checklist.${field}`)}
                </label>
                <input
                    id={id}
                    type={'text'}
                    className={fieldClass}
                    value={row[field]}
                    onChange={(e) => update(row.id, { [field]: e.target.value })}
                />
            </div>
        );
    };

    return (
        <div className={'flex flex-col gap-3'}>
            {rows.length === 0 && (
                <p className={'text-sm text-foreground/60'}>{t('equipment_checklist.empty')}</p>
            )}
            {rows.map((row, index) => (
                <div
                    key={row.id}
                    className={'flex flex-col gap-2 rounded-md border border-foreground/10 p-3'}
                    aria-label={t('equipment_checklist.item', { number: index + 1 })}
                    role={'group'}
                >
                    <div className={'grid grid-cols-1 gap-2 sm:grid-cols-2'}>
                        {input(row, 'labelDe', index)}
                        {input(row, 'labelEn', index)}
                        {input(row, 'noteDe', index)}
                        {input(row, 'noteEn', index)}
                    </div>
                    <div className={'flex justify-end'}>
                        <CustomButton
                            title={t('equipment_checklist.remove')}
                            isFilled={false}
                            onClick={() => remove(row.id)}
                        />
                    </div>
                </div>
            ))}
            <div>
                <CustomButton
                    title={t('equipment_checklist.add')}
                    isFilled={false}
                    onClick={() => onChange([...rows, emptyChecklistRow()])}
                />
            </div>
        </div>
    );
};

export default EquipmentChecklistEditor;
